import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import $ from 'jquery'

export default function useInvoicesList() {
  // Use toast
  const toast = useToast()

  const refInvoiceListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    // Keep selection sticky-left
    {
      key: 'selected',
      label: '',
      stickyColumn: true,
      thClass: 'text-center bg-white',
      tdClass: 'text-center bg-white',
      thStyle: { width: '44px' },
    },
    // Desired order: Invoice Code → Username → Amount → Invoice Date → Last Date → rest
    {
      key: 'invoice_code',
      label: 'Invoice',
      sortable: true,
      thClass: 'text-nowrap',
      tdClass: 'text-nowrap text-primary font-weight-bold',
      thStyle: { width: '120px' },
    },
    {
      key: 'isp_user_name',
      label: 'Username',
      sortable: true,
      thClass: 'text-nowrap',
      tdClass: 'text-nowrap text-dark font-weight-bold',
      thStyle: { width: '140px' },
    },
    {
      key: 'user',
      label: 'Client',
      sortable: false,
      thClass: 'text-nowrap',
      tdClass: 'text-nowrap text-dark',
      thStyle: { minWidth: '160px' },
    },
    {
      key: 'grand_total',
      label: 'Bill',
      sortable: true,
      formatter: val => `${val} BDT`,
      thClass: 'text-nowrap',
      tdClass: 'text-nowrap text-right text-dark font-weight-bold',
      thStyle: { width: '110px' },
    },
    {
      key: 'invoice_date',
      label: 'Inv Date',
      sortable: true,
      thClass: 'text-nowrap d-none d-md-table-cell',
      tdClass: 'text-nowrap text-secondary d-none d-md-table-cell',
      thStyle: { width: '110px' },
    },
    {
      key: 'last_payment_date',
      label: 'Last',
      sortable: true,
      thClass: 'text-nowrap d-none d-lg-table-cell',
      tdClass: 'text-nowrap text-secondary d-none d-lg-table-cell',
      thStyle: { width: '100px' },
    },
    // Rest of the columns
    {
      key: 'payment_status_value',
      label: 'Status',
      sortable: true,
      thClass: 'text-nowrap',
      tdClass: 'text-nowrap text-center',
      thStyle: { width: '90px' },
    },
    {
      key: 'paid_date',
      label: 'Paid',
      sortable: true,
      thClass: 'text-nowrap d-none d-xl-table-cell',
      tdClass: 'text-nowrap text-secondary d-none d-xl-table-cell',
      thStyle: { width: '100px' },
    },
    {
      key: 'payment_method',
      label: 'Method',
      sortable: true,
      thClass: 'text-nowrap d-none d-lg-table-cell',
      tdClass: 'text-nowrap text-dark d-none d-lg-table-cell',
      thStyle: { width: '100px' },
    },
    {
      key: 'payment_note',
      label: 'Note',
      sortable: false,
      thClass: 'text-nowrap d-none d-lg-table-cell',
      tdClass: 'text-nowrap text-dark d-none d-lg-table-cell',
      thStyle: { minWidth: '180px' },
    },
    {
      key: 'actions',
      label: '',
      thClass: 'text-right bg-white sticky-right',
      tdClass: 'text-right text-nowrap bg-white sticky-right',
      thStyle: { width: '120px' },
    },
  ]
  const perPage = ref(100)
  const totalInvoices = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [100, 300, 500, 1000]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const statusFilter = ref(null)
  const ispIdFilter = ref(null)
  const is_pagination = ref(true)
  // const unpaid_amount = ref(0)
  // const paid_amount = ref(0)

  const dataMeta = computed(() => {
    const localItemsCount = refInvoiceListTable.value ? refInvoiceListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalInvoices.value,
    }
  })

  const refetchData = () => {
    refInvoiceListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, statusFilter, ispIdFilter], () => {
    refetchData()
  })

  const fetchInvoices = (ctx, callback) => {
    const sortKey = ctx && ctx.sortBy ? ctx.sortBy : sortBy.value
    const sortDescFlag = ctx && typeof ctx.sortDesc !== 'undefined' ? ctx.sortDesc : isSortDirDesc.value
    const sortKeyMap = {
      id: 'id',
      invoice_code: 'invoice_code',
      isp_user_name: 'isp_user_name',
      user: null,
      grand_total: 'total',
      invoice_date: 'invoice_date',
      last_payment_date: 'last_payment_date',
      paid_date: 'paid_date',
      payment_method: 'payment_method',
      payment_status_value: 'payment_status',
    }
    const apiSortBy = sortKeyMap[sortKey] || sortKey

    const payload = {
      q: searchQuery.value,
      perPage: perPage.value,
      page: currentPage.value,
      ...(apiSortBy ? { sortBy: apiSortBy, sortDesc: sortDescFlag } : {}),
      payment_status: (statusFilter.value) ? statusFilter.value.value : null,
      is_pagination: 'true',
      isp_id: (ispIdFilter.value) ? ispIdFilter.value.value : null,
    }
    store.dispatch('app-invoice/fetchInvoices', payload)
      .then(response => {
        //console.log(response.data.data.invoices.data);
        const invoices  = response.data.data.invoices.data;
        const total = response.data.data.total;
        callback(invoices)
        totalInvoices.value = total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error fetching invoices' list",
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveInvoiceStatusVariantAndIcon = status => {
    if (status === 'Partial Payment') return { variant: 'warning', icon: 'PieChartIcon' }
    if (status === 'Paid') return { variant: 'success', icon: 'CheckCircleIcon' }
    if (status === 'Downloaded') return { variant: 'info', icon: 'ArrowDownCircleIcon' }
    if (status === 'Draft') return { variant: 'primary', icon: 'SaveIcon' }
    if (status === 'Sent') return { variant: 'secondary', icon: 'SendIcon' }
    if (status === 'Past Due') return { variant: 'danger', icon: 'InfoIcon' }
    return { variant: 'secondary', icon: 'XIcon' }
  }

  const resolveClientAvatarVariant = status => {
    if (status === 'Partial Payment') return 'primary'
    if (status === 'Paid') return 'danger'
    if (status === 'Downloaded') return 'secondary'
    if (status === 'Draft') return 'warning'
    if (status === 'Sent') return 'info'
    if (status === 'Past Due') return 'success'
    return 'primary'
  }
  const ispId = $('#ispListId option:selected').val()

  return {
    fetchInvoices,
    tableColumns,
    perPage,
    currentPage,
    totalInvoices,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refInvoiceListTable,
    // unpaid_amount,
    statusFilter,
    ispIdFilter,
    resolveInvoiceStatusVariantAndIcon,
    resolveClientAvatarVariant,

    refetchData,
    ispId,
  }
}
